import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ReservationSummary from '../components/ReservationSummary';
import InsuranceOption from '../components/InsuranceOption';
import AdditionalOption from '../components/AdditionalOption';
import PaymentMethod from '../components/PaymentMethod';
import BookingConfirmationModal from '../components/BookingConfirmationModal';
import InfoModal from '../components/InfoModal';
import { analytics } from '../services/analytics';
import { trackInitiateCheckout } from '../services/metaPixel';
import { bookingService } from '../services/api';
import { useBooking } from '../context/BookingContext';
import { toast } from '../utils/toast';
import './BookingPage.css';

const BookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { 
    selectedVehicle, 
    updateQuoteData, 
    updateBookingDetails
  } = useBooking();
  
  const vehicle = location.state?.vehicle || selectedVehicle;

  const [loading, setLoading] = useState(true);
  const [quoteData, setQuoteData] = useState(null);
  
  const [selectedInsurances, setSelectedInsurances] = useState({
    intermediate: false,
    maximum: false
  });

  const [additionals, setAdditionals] = useState({});

  const [selectedPayment, setSelectedPayment] = useState('credit');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    documentType: 'DNI',
    documentNumber: '',
    street: '',
    number: '',
    postalCode: '',
    country: 'Argentina',
    city: '',
    email: '',
    phone: ''
  });

  const [showModal, setShowModal] = useState(false);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState(null);

  const loadQuote = useCallback(async () => {
    try {
      setLoading(true);
      
      // Validar que tengamos un vehículo
      if (!vehicle || !vehicle.id) {
        console.error('No vehicle selected');
        toast.error('No se seleccionó ningún vehículo');
        navigate('/buscar');
        return;
      }

      const quoteParams = {
        vehicleId: vehicle.id,
        pickupDate: searchParams.get('pickupDate') || new Date().toISOString(),
        returnDate: searchParams.get('returnDate') || new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        pickupLocation: searchParams.get('pickupLocation') || 'Buenos Aires Ezeiza Aeropuerto',
        returnLocation: searchParams.get('returnLocation') || searchParams.get('pickupLocation') || 'Buenos Aires Ezeiza Aeropuerto'
      };

      console.log('Loading quote with params:', quoteParams);
      const response = await bookingService.getQuote(quoteParams);
      console.log('Quote response:', response);

      if (!response.data || !response.data.data) {
        throw new Error('Invalid quote response');
      }

      setQuoteData(response.data.data);
      updateQuoteData(response.data.data);

      // Inicializar adicionales con valores por defecto
      const initialAdditionals = {};
      (response.data.data.additionals || []).forEach(add => {
        initialAdditionals[add.id] = add.required ? 1 : 0;
      });
      setAdditionals(initialAdditionals);
      updateBookingDetails({ additionals: initialAdditionals });

    } catch (error) {
      console.error('Error loading quote:', error);
      toast.error('Error al cargar la cotización. Intenta nuevamente.');
      // No navegar inmediatamente, dar tiempo para ver el error
      setTimeout(() => {
        navigate('/buscar');
      }, 2000);
    } finally {
      setLoading(false);
    }
  }, [vehicle, searchParams, updateQuoteData, updateBookingDetails, navigate]);

  useEffect(() => {
    analytics.trackPageView('booking_page');
    loadQuote();
    
    // Track InitiateCheckout en Meta Pixel
    if (vehicle) {
      trackInitiateCheckout({
        id: vehicle.id,
        name: vehicle.name,
        price: vehicle.pricePerDay
      });
    }
  }, [loadQuote, vehicle]);

  const handleInsuranceChange = (type) => {
    // Solo permitir un seguro a la vez
    const newValue = !selectedInsurances[type];
    const newInsurances = {
      intermediate: false,
      maximum: false,
      [type]: newValue
    };
    setSelectedInsurances(newInsurances);
    updateBookingDetails({ selectedInsurances: newInsurances });
    analytics.trackInsuranceSelected(type, newValue);
  };

  const handleAdditionalChange = (type, value) => {
    const newAdditionals = { ...additionals, [type]: value };
    setAdditionals(newAdditionals);
    updateBookingDetails({ additionals: newAdditionals });
    analytics.trackAdditionalChanged(type, value);
  };

  const handlePaymentChange = (method) => {
    setSelectedPayment(method);
    updateBookingDetails({ paymentMethod: method });
    analytics.trackPaymentMethodSelected(method);
  };

  const handleFormChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    updateBookingDetails({ formData: newFormData });
    if (value) {
      analytics.trackFormFieldFilled(field);
    }
  };

  const openInfoModal = (item) => {
    if (item && item.infoContent) {
      setInfoModalContent({
        title: item.name,
        content: <div dangerouslySetInnerHTML={{ __html: item.infoContent }} />
      });
      setShowInfoModal(true);
    }
  };

  const getPricingItems = () => {
    if (!quoteData) return [];
    
    // Comenzar con los items base del quote
    const items = [...quoteData.pricing.items.map(item => ({
      label: item.label,
      value: `$ ${parseFloat(item.value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
    }))];
    
    // Agregar seguros seleccionados
    (quoteData.insurances || []).forEach(insurance => {
      if (selectedInsurances[insurance.id]) {
        items.push({
          label: `${insurance.name}`,
          value: `$ ${insurance.price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
        });
      }
    });
    
    // Agregar adicionales seleccionados
    (quoteData.additionals || []).forEach(additional => {
      const quantity = additionals[additional.id] || 0;
      if (quantity > 0) {
        const totalPrice = additional.pricePerDay * quantity * quoteData.days;
        items.push({
          label: `(${quantity}) ${additional.name} (ARS $ ${additional.pricePerDay.toLocaleString('es-AR', { minimumFractionDigits: 2 })} por día) x ${quoteData.days} Días`,
          value: `$ ${totalPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`
        });
      }
    });
    
    return items;
  };

  const calculateTotal = () => {
    if (!quoteData) return 0;
    
    let total = parseFloat(quoteData.pricing.total);
    
    // Agregar seguros seleccionados
    (quoteData.insurances || []).forEach(insurance => {
      if (selectedInsurances[insurance.id]) {
        total += insurance.price;
      }
    });
    
    // Agregar adicionales seleccionados
    (quoteData.additionals || []).forEach(additional => {
      const quantity = additionals[additional.id] || 0;
      if (quantity > 0) {
        total += additional.pricePerDay * quantity * quoteData.days;
      }
    });
    
    return total;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'El nombre es requerido';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'El apellido es requerido';
    }

    if (!formData.documentNumber.trim()) {
      errors.documentNumber = 'El número de documento es requerido';
    }

    if (!formData.email.trim()) {
      errors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'El email no es válido';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'El teléfono es requerido';
    }

    if (!formData.street.trim()) {
      errors.street = 'La calle es requerida';
    }

    if (!formData.city.trim()) {
      errors.city = 'La ciudad es requerida';
    }

    if (!termsAccepted) {
      errors.terms = 'Debes aceptar los términos y condiciones';
    }

    setFormErrors(errors);
    
    // Scroll al primer error
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      } else if (errors.terms) {
        // Si el error es de términos, hacer scroll al checkbox
        const termsElement = document.getElementById('terms');
        if (termsElement) {
          termsElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
    
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const bookingData = {
        vehicleId: vehicle?.id,
        vehicleName: vehicle?.name,
        pickupLocation: searchParams.get('pickupLocation') || 'Buenos Aires Ezeiza Aeropuerto',
        returnLocation: searchParams.get('returnLocation') || searchParams.get('pickupLocation') || 'Buenos Aires Ezeiza Aeropuerto',
        pickupDate: searchParams.get('pickupDate') || new Date().toISOString(),
        returnDate: searchParams.get('returnDate') || new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        pickupTime: searchParams.get('pickupTime') || '10:00',
        returnTime: searchParams.get('returnTime') || '10:00',
        customerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          documentType: formData.documentType,
          documentNumber: formData.documentNumber,
          email: formData.email,
          phone: formData.phone,
          address: {
            street: formData.street,
            number: formData.number,
            postalCode: formData.postalCode,
            city: formData.city,
            country: formData.country
          }
        },
        insurances: selectedInsurances,
        additionals,
        paymentMethod: selectedPayment,
        totalPrice: calculateTotal()
      };

      // Crear la reserva en la base de datos
      const response = await bookingService.create(bookingData);
      
      // Track analytics
      analytics.trackBookingAttempt(bookingData);

      // Preparar datos para el modal
      const confirmation = {
        bookingId: response.data.data.id,
        bookingCode: response.data.data.bookingCode,
        vehicleName: vehicle?.name,
        pickupLocation: bookingData.pickupLocation,
        returnLocation: bookingData.returnLocation,
        pickupDate: bookingData.pickupDate,
        returnDate: bookingData.returnDate,
        pickupTime: searchParams.get('pickupTime'),
        returnTime: searchParams.get('returnTime'),
        insurances: selectedInsurances,
        additionals,
        customerName: `${formData.firstName} ${formData.lastName}`,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        totalPrice: calculateTotal(),
        paymentMethod: selectedPayment === 'credit' ? 'Tarjeta de Crédito/Débito' : 
                      selectedPayment === 'installments' ? 'Tarjeta en Cuotas' : 
                      'Pago en Destino'
      };

      setBookingConfirmation(confirmation);
      setShowModal(true);

    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Hubo un error al procesar tu reserva. Por favor intenta nuevamente.');
    }
  };

  const handlePrint = () => {
    // Asegurar que el contexto tenga los datos actualizados
    updateBookingDetails({
      selectedInsurances,
      additionals,
      formData,
      paymentMethod: selectedPayment
    });
    
    // Navegar a la página de impresión
    navigate('/imprimir');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    navigate('/');
  };

  const formatDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr);
    const [hours, minutes] = (timeStr || '00:00').split(':');
    
    return `${date.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short'
    })}, ${hours}:${minutes}`;
  };

  if (loading) {
    return (
      <div className="booking-page">
        <div className="booking-container">
          <div className="loading">Cargando cotización...</div>
        </div>
      </div>
    );
  }

  if (!quoteData) {
    return (
      <div className="booking-page">
        <div className="booking-container">
          <div className="error">Error al cargar la cotización</div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <div className="booking-container">
        <div className="booking-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Elegir otro auto
          </button>
          <div className="header-actions">
            <button className="print-button" onClick={handlePrint}>
              🖨️ Imprimir
            </button>
          </div>
        </div>

        <div className="booking-content">
          <div className="booking-form">
            <section className="form-section">
              <h2>Seguros</h2>
              {quoteData.insurances.map((insurance) => {
                const hasInfo = insurance.infoContent;
                
                return (
                  <InsuranceOption
                    key={insurance.id}
                    icon={insurance.icon}
                    title={insurance.name}
                    description={insurance.description}
                    price={`$ ${insurance.price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`}
                    link={hasInfo ? { text: 'Leer más', url: '#' } : null}
                    selected={selectedInsurances[insurance.id]}
                    onChange={() => handleInsuranceChange(insurance.id)}
                    onReadMore={hasInfo ? () => openInfoModal(insurance) : undefined}
                  />
                );
              })}
            </section>

            <section className="form-section">
              <h2>Adicionales</h2>
              {quoteData.additionals.map((additional) => {
                const hasInfo = additional.infoContent;
                
                return (
                  <div 
                    key={additional.id}
                    className={additional.required ? 'highlighted-option' : ''}
                  >
                    <AdditionalOption
                      icon={additional.icon}
                      title={additional.name}
                      description={additional.description}
                      price={`$ ${additional.pricePerDay.toLocaleString('es-AR', { minimumFractionDigits: 2 })} por día`}
                      quantity={additionals[additional.id] || 0}
                      onQuantityChange={(val) => handleAdditionalChange(additional.id, val)}
                      link={hasInfo ? { text: 'Leer más', url: '#' } : null}
                      onReadMore={hasInfo ? () => openInfoModal(additional) : undefined}
                      maxQuantity={additional.maxQuantity || 1}
                    />
                  </div>
                );
              })}
            </section>

            {quoteData.otherServices && quoteData.otherServices.length > 0 && (
              <section className="form-section">
                <h2>Otros</h2>
                {quoteData.otherServices.map((service) => (
                  <div 
                    key={service.id}
                    className={service.required ? 'highlighted-option' : ''}
                  >
                    <InsuranceOption
                      icon={service.icon}
                      title={service.name}
                      description={service.description}
                      price={`$ ${service.pricePerDay.toLocaleString('es-AR', { minimumFractionDigits: 2 })} por día`}
                      selected={true}
                      onChange={() => {}}
                    />
                  </div>
                ))}
              </section>
            )}

            <section className="form-section">
              <h2>Datos del conductor y contacto</h2>
              <form onSubmit={handleSubmit} className="driver-form">
                <div className="form-columns">
                  <div className="form-column">
                    <h3>Datos del conductor</h3>
                    <div className={`form-field ${formErrors.firstName ? 'has-error' : ''}`}>
                      <input 
                        type="text" 
                        name="firstName"
                        placeholder="Nombre *" 
                        required 
                        value={formData.firstName}
                        onChange={(e) => handleFormChange('firstName', e.target.value)}
                        className={formErrors.firstName ? 'error' : ''}
                      />
                      {formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}
                    </div>
                    <div className={`form-field ${formErrors.lastName ? 'has-error' : ''}`}>
                      <input 
                        type="text" 
                        name="lastName"
                        placeholder="Apellido *" 
                        required 
                        value={formData.lastName}
                        onChange={(e) => handleFormChange('lastName', e.target.value)}
                        className={formErrors.lastName ? 'error' : ''}
                      />
                      {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
                    </div>
                    <div className="form-field-group">
                      <select 
                        required
                        value={formData.documentType}
                        onChange={(e) => handleFormChange('documentType', e.target.value)}
                      >
                        <option value="">Tipo *</option>
                        <option value="DNI">DNI</option>
                        <option value="Pasaporte">Pasaporte</option>
                      </select>
                      <div className={`form-field ${formErrors.documentNumber ? 'has-error' : ''}`} style={{flex: 1}}>
                        <input 
                          type="text" 
                          name="documentNumber"
                          placeholder="N° Documento *" 
                          required 
                          value={formData.documentNumber}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            handleFormChange('documentNumber', value);
                          }}
                          pattern="[0-9]*"
                          inputMode="numeric"
                          className={formErrors.documentNumber ? 'error' : ''}
                        />
                        {formErrors.documentNumber && <span className="error-message">{formErrors.documentNumber}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="form-column">
                    <h3>Datos de contacto</h3>
                    <div className="form-field-group">
                      <div className={`form-field ${formErrors.street ? 'has-error' : ''}`} style={{flex: 1}}>
                        <input 
                          type="text" 
                          name="street"
                          placeholder="Calle *" 
                          required 
                          value={formData.street}
                          onChange={(e) => handleFormChange('street', e.target.value)}
                          className={formErrors.street ? 'error' : ''}
                        />
                        {formErrors.street && <span className="error-message">{formErrors.street}</span>}
                      </div>
                      <input 
                        type="text" 
                        placeholder="Número *" 
                        required 
                        style={{ maxWidth: '120px' }}
                        value={formData.number}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          handleFormChange('number', value);
                        }}
                        pattern="[0-9]*"
                        inputMode="numeric"
                      />
                    </div>
                    <div className="form-field-group">
                      <input 
                        type="text" 
                        placeholder="Cód. Postal *" 
                        required 
                        value={formData.postalCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          handleFormChange('postalCode', value);
                        }}
                        pattern="[0-9]*"
                        inputMode="numeric"
                      />
                      <select 
                        required
                        value={formData.country}
                        onChange={(e) => handleFormChange('country', e.target.value)}
                      >
                        <option value="">País *</option>
                        <option value="Argentina">Argentina</option>
                      </select>
                    </div>
                    <div className={`form-field ${formErrors.city ? 'has-error' : ''}`}>
                      <input 
                        type="text"
                        name="city"
                        placeholder="Ciudad *"
                        required
                        value={formData.city}
                        onChange={(e) => handleFormChange('city', e.target.value)}
                        className={formErrors.city ? 'error' : ''}
                      />
                      {formErrors.city && <span className="error-message">{formErrors.city}</span>}
                    </div>
                    <div className={`form-field ${formErrors.email ? 'has-error' : ''}`}>
                      <input 
                        type="email" 
                        name="email"
                        placeholder="Correo Electrónico *" 
                        required 
                        value={formData.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                        className={formErrors.email ? 'error' : ''}
                      />
                      {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                    </div>
                    <div className={`form-field ${formErrors.phone ? 'has-error' : ''}`}>
                      <input 
                        type="tel" 
                        name="phone"
                        placeholder="Teléfono *" 
                        required 
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          handleFormChange('phone', value);
                        }}
                        pattern="[0-9]*"
                        inputMode="numeric"
                        className={formErrors.phone ? 'error' : ''}
                      />
                      {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                    </div>
                  </div>
                </div>
              </form>
            </section>

            <section className="form-section">
              <h2>Métodos de pago</h2>
              <PaymentMethod
                icon="💳"
                title="Tarjeta de Crédito y Débito"
                originalPrice={calculateTotal()}
                finalPrice={calculateTotal() * 0.85}
                selected={selectedPayment === 'credit'}
                onChange={() => handlePaymentChange('credit')}
              />
              <PaymentMethod
                icon="💳"
                title="Hasta 12 cuotas con interés con Tarjeta de Crédito"
                originalPrice={calculateTotal()}
                finalPrice={calculateTotal()}
                selected={selectedPayment === 'installments'}
                onChange={() => handlePaymentChange('installments')}
              />
              <PaymentMethod
                icon="🏢"
                title="Pago en Destino"
                finalPrice={calculateTotal()}
                selected={selectedPayment === 'destination'}
                onChange={() => handlePaymentChange('destination')}
              />
              
              <div className={`terms-checkbox ${formErrors.terms ? 'has-error' : ''}`}>
                <input 
                  type="checkbox" 
                  id="terms" 
                  required 
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <label htmlFor="terms">
                  Leí y acepto los Términos y condiciones y las Políticas de privacidad
                </label>
                {formErrors.terms && <span className="error-message">{formErrors.terms}</span>}
              </div>

              <button type="submit" className="submit-button" onClick={handleSubmit}>
                CONFIRMAR RESERVA
              </button>
            </section>
          </div>

          <aside className="booking-sidebar">
            <ReservationSummary
              vehicle={{
                name: vehicle?.name || 'Fiat Argo o similar',
                category: vehicle?.category || 'Categoría Sedán Económico MT',
                image: vehicle?.image || '/placeholder-car.svg'
              }}
              details={{
                duration: `${quoteData.days} Días`,
                guarantees: quoteData.guarantees,
                pickup: {
                  location: searchParams.get('pickupLocation') || 'Buenos Aires Ezeiza Aeropuerto',
                  icon: quoteData.pickupLocationData?.icon || '📍',
                  datetime: formatDateTime(
                    searchParams.get('pickupDate') || new Date().toISOString(),
                    searchParams.get('pickupTime')
                  )
                },
                return: {
                  location: searchParams.get('returnLocation') || searchParams.get('pickupLocation') || 'Buenos Aires Ezeiza Aeropuerto',
                  icon: quoteData.returnLocationData?.icon || '📍',
                  datetime: formatDateTime(
                    searchParams.get('returnDate') || new Date().toISOString(),
                    searchParams.get('returnTime')
                  )
                }
              }}
              pricing={{
                items: getPricingItems(),
                total: calculateTotal(),
                promoCode: true,
                savings: parseFloat(quoteData.pricing.savings)
              }}
            />
          </aside>
        </div>
      </div>

      <BookingConfirmationModal 
        isOpen={showModal}
        onClose={handleCloseModal}
        bookingData={bookingConfirmation}
      />

      <InfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title={infoModalContent?.title}
        content={infoModalContent?.content}
      />
    </div>
  );
};

export default BookingPage;
